import { useCallback, useEffect, useRef, useState } from 'react'

import { ArrowLeft, ArrowRight, Bread } from '@phosphor-icons/react'
import { useMutation, useSuspenseQueries } from '@tanstack/react-query'
import {
  createFileRoute,
  Link,
  linkOptions,
  redirect,
} from '@tanstack/react-router'
import {
  MediaControlBar,
  MediaController,
  MediaFullscreenButton,
  MediaMuteButton,
  MediaPlayButton,
  MediaTimeDisplay,
  MediaTimeRange,
  MediaVolumeRange,
} from 'media-chrome/react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import {
  TaskLayout,
  TaskLayoutBreadcrumbs,
  TaskLayoutContent,
  TaskLayoutQuestion,
  TaskLayoutQuestionCard,
  TaskLayoutQuestionPagination,
  TaskLayoutStepper,
} from '@/components/view/task/task-layout'
import {
  IStepperStatus,
  Stepper,
} from '@/components/view/task/task-stepper-simple'
import { ILessonItem } from '@/components/view/task/type'
import { useTaskLesson } from '@/components/view/task/use-task-lesson'

import { useAuth } from '@/services/auth/auth.store'
import {
  trackDetails,
  trackLessonList,
  trackLessonMarkAsComplete,
  trackList,
} from '@/services/track/track.gql'

import { formatDateServerTime } from '@/lib/format'
import { invalidateQueries } from '@/lib/utils'

export const Route = createFileRoute('/(app)/track/$trackid/lesson/$lessonid')({
  async loader({ params, context }) {
    useAuth.getState().access('authenticated')
    const { trackid, lessonid } = params

    const myTracks = await context.qc.ensureQueryData(
      trackList.qry({ filterMyClass: 'All', myClass: true })
    )
    const trackIds = myTracks.getTracksWithPagination.data.map((t) => t.id)

    if (!trackIds.includes(trackid)) {
      toast.error('Track tidak ada di kelas kamu')
      return redirect({
        from: Route.fullPath,
        to: '/track/$trackid',
      })
    }

    await useTaskLesson.getState().reset()
    await useTaskLesson.getState().setup(trackid, lessonid)
  },
  component: RouteComponent,
})

function RouteComponent() {
  return (
    <TaskLayout>
      <Breadcrumbs />
      <Content />
    </TaskLayout>
  )
}

function Content() {
  return (
    <TaskLayoutContent>
      <LessonStepper />
      <ContentQuestion />
    </TaskLayoutContent>
  )
}

function LessonStepper() {
  const lessonList = useTaskLesson((s) => s.lessonsList)
  const selected = useTaskLesson((s) => s.selected)
  const chapterTitle = useTaskLesson((s) => s.chapterTitle)

  return (
    <TaskLayoutStepper className="top-[5rem] h-min grow-0 overflow-auto rounded-lg bg-white pt-2 pb-1 lg:sticky lg:py-3">
      <h2 className="mb-1 px-2 text-lg font-semibold">{chapterTitle}</h2>
      <Stepper className="overflow-auto lg:max-h-[60vh]">
        {lessonList.map((l, index) => {
          const data = selected?.id && l.id === selected.id ? selected : l
          let status: IStepperStatus = 'enable'
          if (data.status === 'correct') status = 'complete'
          if (selected?.id && data.id === selected.id) status = 'focus'

          return (
            <Stepper.Item
              key={l.id}
              state={{
                status,
                label: data.title,
                desc: `Lesson ${index + 1}`,
                onClick: () => {
                  useTaskLesson.getState().onChangeLesson(data.index)
                },
              }}
            />
          )
        })}
      </Stepper>
    </TaskLayoutStepper>
  )
}

function ContentQuestion() {
  const selected = useTaskLesson((s) => s.selected)

  return (
    <TaskLayoutQuestion className="flex-col-reverse gap-4 lg:flex-col">
      <TaskLayoutQuestionCard className="relative">
        {selected && <LessonContent {...selected} />}
      </TaskLayoutQuestionCard>
      <TaskLayoutQuestionPagination>
        <PaginationArrow />
      </TaskLayoutQuestionPagination>
    </TaskLayoutQuestion>
  )
}

function LessonContent(props: ILessonItem) {
  return (
    <div className="flex grow flex-col gap-4 pb-8">
      <LessonTypeVideo {...props} />
    </div>
  )
}

/**
 * Rules:
 * - On Play marked as complete
 * - On Next button clicked, mark as complete
 */

function LessonTypeVideo(props: ILessonItem) {
  const lessonId = useTaskLesson((s) => s.lessonId)
  const { lessonContent, title, id } = props
  const { mutateAsync } = useMutation({
    mutationFn: trackLessonMarkAsComplete.api,
    onSuccess() {
      invalidateQueries(trackDetails.key)
      useTaskLesson.setState((x) => ({
        selected: {
          ...x.selected,
          status: 'correct',
        },
      }))
    },
  })

  const onComplete = useCallback(() => {
    const now = new Date()
    mutateAsync({
      chapterId: lessonId,
      completedAt: formatDateServerTime(now),
      startedAt: formatDateServerTime(now),
      lessonId: id,
    })
  }, [mutateAsync, id, lessonId])

  return (
    <div className="relative flex flex-col gap-4">
      {!!lessonContent.link_video && (
        <VideoReactPlayer
          src={lessonContent.link_video}
          key={lessonContent.link_video}
          onComplete={onComplete}
        />
      )}
      <div className="flex w-full grow flex-col gap-2 overflow-auto rounded-lg border border-[#FFC28B] bg-yellow-500/10 p-3">
        <h2 className="text-lg font-bold">{title}</h2>
        {lessonContent.description && (
          <div
            dangerouslySetInnerHTML={{ __html: lessonContent.description }}
            className="max-w-full overflow-x-auto"
          />
        )}
      </div>
    </div>
  )
}

function VideoHtml({
  src,
  onComplete,
}: {
  src: string
  onComplete: () => void
}) {
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const [hasReported, setHasReported] = useState(false)

  useEffect(() => {
    const video = videoRef.current
    if (!video) return

    const handlePlay = () => {
      if (!video || hasReported) return

      onComplete()
      setHasReported(true)
    }

    video.addEventListener('play', handlePlay)
    return () => {
      video.removeEventListener('play', handlePlay)
    }
  }, [hasReported, onComplete])

  return (
    <div className="relative my-3 aspect-video">
      <video
        id="video"
        onContextMenu={(e) => e.preventDefault()}
        controls
        className="h-full w-full object-cover"
        controlsList="nodownload noremoteplayback noplaybackrate"
        disablePictureInPicture
        key={src}
        ref={videoRef}
      >
        <source src={src} />
        Your browser does not support the video tag.
      </video>
    </div>
  )
}

function VideoReactPlayer({
  src,
  onComplete,
}: {
  src: string
  onComplete: () => void
}) {
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const [hasReported, setHasReported] = useState(false)

  useEffect(() => {
    const video = videoRef.current
    if (!video) return

    const handlePlay = () => {
      if (!video || hasReported) return

      onComplete()
      setHasReported(true)
    }

    video.addEventListener('play', handlePlay)
    return () => {
      video.removeEventListener('play', handlePlay)
    }
  }, [hasReported, onComplete])

  return (
    <div className="relative my-3 aspect-video">
      <MediaController className="aspect-video w-full">
        <video
          slot="media"
          src={src}
          preload="auto"
          ref={videoRef}
          onContextMenu={(e) => e.preventDefault()}
          className="h-full w-full"
        />
        <MediaControlBar className="flex">
          <MediaPlayButton className="px-2"></MediaPlayButton>
          <MediaTimeRange className="pl-1"></MediaTimeRange>
          <MediaTimeDisplay className="pr-1" showDuration></MediaTimeDisplay>
          <MediaMuteButton className="pl-1"></MediaMuteButton>
          <MediaVolumeRange className="pr-1"></MediaVolumeRange>
          <MediaFullscreenButton className="px-2"></MediaFullscreenButton>
        </MediaControlBar>
      </MediaController>
    </div>
  )
}

function PaginationArrow() {
  const lessonId = useTaskLesson((s) => s.lessonId)
  const selected = useTaskLesson((s) => s.selected)
  const lessonsCount = useTaskLesson((s) => s.lessonsCount)
  const onChangeLesson = useTaskLesson((s) => s.onChangeLesson)

  const { mutateAsync } = useMutation({
    mutationFn: trackLessonMarkAsComplete.api,
    onSuccess() {
      invalidateQueries(trackDetails.key)
      useTaskLesson.setState((x) => ({
        selected: {
          ...x.selected,
          status: 'correct',
        },
      }))
    },
  })

  const isFirst = selected?.index === 0
  const isLast = selected?.index === lessonsCount - 1

  const onNext = async () => {
    if (selected.status !== 'correct') {
      const now = new Date()
      await mutateAsync({
        chapterId: lessonId,
        completedAt: formatDateServerTime(now),
        startedAt: formatDateServerTime(now),
        lessonId: selected.id,
      })
    }
    onChangeLesson(selected.index + 1)
  }

  const onPrev = () => {
    onChangeLesson(selected.index - 1)
  }

  return (
    <div className="flex grow justify-between gap-2 px-4">
      {!isFirst && (
        <Button
          size={'icon'}
          variant={'outline'}
          type="button"
          className="mr-auto"
          onClick={onPrev}
          // disabled={isValidating}
        >
          <ArrowLeft />
        </Button>
      )}
      {!isLast && (
        <Button
          size={'icon'}
          variant={'outline'}
          type="button"
          className="ml-auto"
          onClick={onNext}
        >
          <ArrowRight />
        </Button>
      )}
      {isLast && (
        <Button
          variant={'yes'}
          type="button"
          className="ml-auto"
          onClick={onNext}
        >
          <Link to="../.." from={Route.fullPath}>
            Selesai
          </Link>
        </Button>
      )}
    </div>
  )
}

function useData() {
  const params = Route.useParams()
  const [track, lesson] = useSuspenseQueries({
    queries: [
      trackDetails.qry({ trackId: params.trackid, myTrack: true }),
      trackLessonList.qry({ chapterId: params.lessonid }),
    ],
  })

  const chapter = lesson.data.listLessonFromDetailChapter.lessons[0]!

  return {
    track: track.data,
    lesson: lesson.data.listLessonFromDetailChapter,
    chapter,
  }
}

function Breadcrumbs() {
  const { track } = useData()
  const chapterTitle = useTaskLesson((s) => s.chapterTitle)

  return (
    <TaskLayoutBreadcrumbs
      items={[
        ['Kelas', linkOptions({ from: Route.fullPath, to: '/' })],
        [
          track.getDetailTrack.title,
          linkOptions({ from: Route.fullPath, to: '/track/$trackid' }),
        ],
        [
          chapterTitle,
          linkOptions({
            from: Route.fullPath,
            to: '/track/$trackid/lesson/$lessonid',
          }),
        ],
      ]}
    />
  )
}
